import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ReportV5Detailed.css';
// 复用粗略报告的 v5r- 类（.v5r-page-head / .v5r-divider / .v5r-section-name 等），让两份
// 报告页眉视觉统一。CSS 名称 v5r- 与 detailed 自己的 v5d- 前缀不冲突。
import '../report_v5/ReportV5.css';
import ReportIcon from '../report_v5/ReportIcons';
import ReportShell, { ReportDownloadButton } from '../report_v5/ReportShell';

// 目录条目：id 必须与下方各 Page 的 <section id> 一一对应（scroll-spy 靠它）
const DETAILED_SECTIONS = [
  { id: 'v5d-sec-overview', label: '总览' },
  { id: 'v5d-sec-model', label: '模型依据' },
  { id: 'v5d-sec-abilities', label: '5 力深度诊断' },
  { id: 'v5d-sec-scenarios', label: '管理场景应用' },
  { id: 'v5d-sec-growth', label: '提升路径' },
];

/**
 * V5 详细报告（付费 5-Page）。
 *
 * 入参：
 *  - vm:           reportV5DetailedAdapter 出的 view model（含 detailed 子块）
 *  - assessmentId: 'preview' / 真 id
 *
 * 5 个 Page 顺序流式排列（同一卷轴）：
 *   1 总览 · 2 模型说明 · 3 五力深度诊断 · 4 管理场景应用建议 · 5 个性化提升路径
 * （素质诊断页已永久移除——领导力测评不含素质内容）
 *
 * 视觉参考：docs/design/报告页1-6(详细).png
 *
 * 品牌字串：**前哨科技 QianShao.AI** —— 注意设计稿 page 5/6 角标写成"前瞻科技"是 typo，
 * 实际全部统一为"前哨科技"。
 */

// 品牌字串备用常量（不在 header 里展示，footer / about / PDF 等地方可用）。
// 设计稿 P5/P6 角标 typo "前瞻" 不被引入代码，统一使用"前哨"。
// eslint-disable-next-line no-unused-vars
const BRAND_CN = '前哨科技';
// eslint-disable-next-line no-unused-vars
const BRAND_EN = 'QianShao.AI';

export default function ReportV5Detailed({ vm, assessmentId, onBack, printMode = false }) { // eslint-disable-line no-unused-vars
  if (!vm?.is_detailed) {
    return (
      <div className={`v5d-root${printMode ? ' print-mode' : ''}`}>
        <div className="v5d-container">
          {onBack && !printMode && <BackBar onBack={onBack} />}
          <div className="v5d-state">
            <div className="v5d-state-icon"><ReportIcon name="lock" size={28} /></div>
            <h2>详细报告未解锁</h2>
            <p>当前报告为粗略版（免费）。解锁付费报告后可查看完整 5 Page 详细解读。</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`v5d-root${printMode ? ' print-mode' : ''}`}>
      <ReportShell
        sections={DETAILED_SECTIONS}
        printMode={printMode}
        footer={<ReportDownloadButton assessmentId={assessmentId} variant="detailed" />}
      >
        <div className="v5d-container">
          {onBack && !printMode && <BackBar onBack={onBack} />}
          {vm.is_detailed_fallback && <FallbackBanner />}
          <Page1Overview vm={vm} />
          <Page2Model vm={vm} />
          <Page3Abilities vm={vm} />
          <Page4Scenarios vm={vm} />
          <Page5Growth vm={vm} />
        </div>
      </ReportShell>
    </div>
  );
}

// 详细报告下载按钮已抽到 ReportShell 的 ReportDownloadButton（variant=detailed），
// 固定在目录底部；这里不再单独渲染。

function BackBar({ onBack }) {
  const navigate = useNavigate();
  const btnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    background: '#fff',
    border: '1px solid var(--v5-line, #e6e6ec)',
    borderRadius: 10,
    color: 'var(--v5-ink-2, #3a3a45)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(15, 35, 80, 0.04)',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button type="button" onClick={onBack} style={btnStyle}>
        <ReportIcon name="arrow-left" size={16} />
        <span>返回基础版报告</span>
      </button>
      <button type="button" onClick={() => navigate('/')} style={btnStyle}>
        <ReportIcon name="home" size={16} />
        <span>返回首页</span>
      </button>
    </div>
  );
}

// ===========================================================================
// 共享子组件
// ===========================================================================

function FallbackBanner() {
  return (
    <div className="v5d-fallback-banner">
      <ReportIcon name="lightbulb" size={18} />
      <div>
        <b>降级版本：</b>本次报告由后端 fallback 模板生成（LLM 输出未通过硬校验）。
        分数与排名均为真实计算结果，文案为模板兜底，建议联系管理员重新生成。
      </div>
    </div>
  );
}

// 详细报告页眉 —— 与粗略报告 ReportV5 的 PageHeader 视觉统一。
// 旧版有独立 BrandRow（前哨科技 + 01/06 页码）和大数字徽章，view review 后判定
// 跟粗略不一致，统一改为「AI领导力测评报告（专业版）| <section>」+ 副标题 + 右上图标。
// 品牌字串保留为常量备用（footer / about 处可用），但页眉不再单独显示。
// pageIndex 参数保留以便未来需要时（如 PDF 模板）使用。
function PageHeader({ pageIndex, section, subtitle, icon }) { // eslint-disable-line no-unused-vars
  return (
    <header className="v5r-page-head" style={{ borderBottom: 'none', marginBottom: 0 }}>
      <div>
        <h1>
          AI领导力测评报告（专业版）
          <span className="v5r-divider">|</span>
          <span className="v5r-section-name">{section}</span>
        </h1>
        <div className="v5r-sub">{subtitle}</div>
      </div>
      <div className="v5d-head-icon">
        <ReportIcon name={icon} size={36} strokeWidth={1.3} />
      </div>
    </header>
  );
}

function MetaBar({ meta }) {
  return (
    <div className="v5d-meta">
      <MetaItem icon="users" label="测评人" value={meta.nickname} />
      {meta.position && <MetaItem icon="user-check" label="岗位" value={meta.position} />}
      <MetaItem icon="award" label="版本" value={meta.version_label} />
      <MetaItem icon="clipboard" label="作答" value={`${meta.answers_done}/${meta.answers_total}`} />
      <MetaItem icon="book-open" label="生成时间" value={meta.generated_at} />
    </div>
  );
}

function NextStepItem({ pageno, icon, title, desc }) {
  return (
    <div className="v5d-nextstep-item">
      <div className="v5d-nextstep-head">
        <ReportIcon name={icon} size={14} />
        <span className="v5d-nextstep-pageno">P{pageno}</span>
      </div>
      <div className="v5d-nextstep-title">{title}</div>
      <div className="v5d-nextstep-desc">{desc}</div>
    </div>
  );
}

function MetaItem({ icon, label, value }) {
  return (
    <span className="v5d-meta-item">
      <span className="v5d-meta-icon"><ReportIcon name={icon} size={13} /></span>
      <span className="v5d-meta-label">{label}：</span>
      <span className="v5d-meta-val">{value || '—'}</span>
    </span>
  );
}

// ===========================================================================
// Page 1 · 总览
// ===========================================================================
function Page1Overview({ vm }) {
  // 优势/风险焦点：复用与基础版报告、后端排名一致的口径 —— strength_abilities(top2)/
  // improvement_abilities(bottom2),两组天然互斥。旧实现按 5 项分数各取 top3+bottom3,
  // 而 5 项时中位项必然同时落进两边(线上即「AI 共生」72 同时出现在优势与风险),
  // 这是前端取数逻辑问题,后端早已给出互斥的 2+2 排名。
  // 这两个字段仅含 {code,name},按 code 从 abilities 补回分数;老报告/降级件字段为空时
  // 退回按分数排名的 top2/bottom2(同样互斥),并对风险侧再做一次去重兜底。
  const byCode = new Map(vm.abilities.map((a) => [a.code, a]));
  const ranked = vm.abilities.slice().sort((a, b) => b.percentage - a.percentage);
  const enrich = (list) => list.map((a) => byCode.get(a.code) || a);
  const strengths = enrich(
    vm.strength_abilities && vm.strength_abilities.length ? vm.strength_abilities : ranked.slice(0, 2)
  );
  const strengthCodes = new Set(strengths.map((a) => a.code));
  const risks = enrich(
    vm.improvement_abilities && vm.improvement_abilities.length
      ? vm.improvement_abilities
      : ranked.slice(-2).reverse()
  ).filter((a) => !strengthCodes.has(a.code));

  return (
    <section className="v5d-page" id="v5d-sec-overview">
      <PageHeader pageIndex={1} section="总览" subtitle="结合你的得分给出整体画像与关键焦点" icon="bar-chart-3" />
      <MetaBar meta={vm.meta} />

      <div className="v5d-grid-2" style={{ marginBottom: 18 }}>
        {/* 总体诊断 */}
        <div className="v5d-overall-card">
          <div className="v5d-card-title">
            <ReportIcon name="shield" size={16} /> 总体诊断
          </div>
          <div className="v5d-overall-l-row">
            <span className="v5d-overall-l">{vm.overall.l_grade || '—'}</span>
            <span className="v5d-overall-l-name">{vm.overall.l_grade_label}</span>
          </div>
          <div className="v5d-overall-score">
            综合得分：
            <span className="v5d-overall-score-big">{vm.overall.percentage}</span>
            <span className="v5d-overall-score-of">/ 100</span>
          </div>
          {vm.overall.summary && (
            <div className="v5d-overall-summary">
              <ReportIcon name="lightbulb" size={16} />
              <span>{vm.overall.summary}</span>
            </div>
          )}
        </div>

        {/* 关键诊断 */}
        <div className="v5d-stack">
          <div className="v5d-focus-block strength">
            <div className="v5d-card-title strength">
              <ReportIcon name="thumbs-up" size={16} /> 优势焦点
            </div>
            <div className="v5d-focus-list">
              {strengths.map((a) => (
                <div key={a.code} className="v5d-focus-item">
                  <span>{a.name}</span>
                  <span className="v5d-pct">{a.percentage} / 100</span>
                </div>
              ))}
            </div>
          </div>
          <div className="v5d-focus-block risk">
            <div className="v5d-card-title risk">
              <ReportIcon name="alert-triangle" size={16} /> 风险焦点
            </div>
            <div className="v5d-focus-list">
              {risks.map((a) => (
                <div key={a.code} className="v5d-focus-item">
                  <span>{a.name}</span>
                  <span className="v5d-pct">{a.percentage} / 100</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 5 力概览 */}
      <div className="v5d-card" style={{ marginBottom: 18 }}>
        <div className="v5d-card-title">
          <ReportIcon name="bar-chart-3" size={16} /> 5 力成熟度概览
        </div>
        <div className="v5d-layer-overview">
          {vm.abilities.map((a) => (
            <div key={a.code} className="v5d-layer-card">
              <h4>{a.name}</h4>
              <div className="v5d-layer-stat">
                <span className="v5d-l">{a.l_grade_label || a.l_grade || '—'}</span>
                <span className="v5d-pct">{a.percentage} / 100</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 本报告导读 —— 后几个章节的内容简介 */}
      <div className="v5d-nextstep">
        <div className="v5d-card-title">
          <ReportIcon name="book-open" size={16} /> 本报告导读
        </div>
        <p className="v5d-nextstep-intro">
          接下来的 4 个章节会从模型、能力、场景到提升路径，把你的 AI 领导力画像拆细呈现。建议按顺序阅读，先了解模型立论，再对照自己的得分逐步看深处。
        </p>
        <div className="v5d-nextstep-list">
          <NextStepItem pageno="02" icon="file-text" title="模型依据"  desc="本报告所依据的模型来源、结构与立论说明。" />
          <NextStepItem pageno="03" icon="bar-chart-3" title="5 力深度诊断" desc="5 项核心能力逐项分析，含分数、影响与提升方向。" />
          <NextStepItem pageno="04" icon="users" title="管理场景应用" desc="结合管理场景给出 AI 使用边界与协作方式建议。" />
          <NextStepItem pageno="05" icon="trending-up" title="提升路径" desc="结合你的结果定制的 30 天分阶段提升计划。" />
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// Page 2 · 模型依据与报告说明
// 严格按 docs/design/报告页2(详细).png + docs/design/前哨AI胜任力模型.docx 的「模型来源
// 与权威基础」「模型结构」「四级标准」「使用提醒」节录入。
// ===========================================================================
const MODEL_PILLARS = [
  {
    name: '国际研究文献',
    desc: '吸收 Long & Magerko AI Literacy 框架、Ng 等 AI 素养四维模型、UNESCO AI Competency Framework、欧盟 DigComp 2.2 等国际研究，奠定理论底座。',
  },
  {
    name: '测评量表与实证研究',
    desc: '参考 MAILS、SNAIL、AISES、GLAT、GAIL 等已发表的 AI 素养 / 自我效能 / 生成式 AI 素养量表，确保各维度的可测性。',
  },
  {
    name: '中国政策与产业语境',
    desc: '对接中央网信办《提升全民数字素养与技能行动纲要》、人社部新职业目录、信通院《人工智能发展报告》等本地政策与产业研究。',
  },
  {
    name: '企业实际场景',
    desc: '在真实企业的 AI 人才盘点、岗位能力建模与培训设计中迭代验证，确保模型不停留在文献，而能回到一线落地。',
  },
];

const MODEL_LAYERS = [
  {
    name: 'AI借力',
    question: '能否识别并善用 AI 工具提升个人工作效率',
    items: ['工具选用', '效率放大', '场景匹配'],
  },
  {
    name: 'AI共生',
    question: '能否与 AI 协作完成复杂任务，保持人机有效配合',
    items: ['人机协同', '指令交互', '任务编排'],
  },
  {
    name: 'AI带队',
    question: '能否带领团队建立 AI 协作文化与工作流',
    items: ['团队赋能', '流程设计', '变革推动'],
  },
  {
    name: 'AI治理',
    question: '能否识别 AI 风险、守住质量与安全边界',
    items: ['结果评估', '安全意识', '风险管控'],
  },
  {
    name: 'AI谋势',
    question: '能否从战略视角洞察 AI 趋势并做出前瞻决策',
    items: ['趋势判断', '战略布局', '创新转化'],
  },
];

const MODEL_RESULT_NOTES = [
  {
    name: '总体得分（4 时期）',
    desc: '综合得分映射到 4 时期（初用期 / 惯用期 / 扩散期 / 重构期），刻画整体 AI 应用成熟度水平。',
  },
  {
    name: '5 力独立得分',
    desc: '5 项核心能力各自独立给出百分制得分与所处时期，按 4 时期（初用 / 嵌入 / 带动 / 重构）刻画各力成熟度，便于看清个人强弱分布与提升方向。',
  },
];

const MODEL_NOTICES = [
  {
    title: '用于成长参考',
    body: '本报告用于个人 AI 应用能力的成长参考，**不作为绩效评估或人事决策依据**。',
  },
  {
    title: '结合工作场景使用',
    body: '建议结合自身真实工作任务回头复盘，把每项能力的提升点落到具体场景里。',
  },
  {
    title: '定期复测追踪',
    body: '能力会随练习与场景变化，建议每 **2-3 个月** 重新测评一次，跟踪自身的能力轨迹。',
  },
  {
    title: '保持持续学习',
    body: 'AI 工具与方法迭代快，模型与本报告也会持续优化，建议保持定期更新自己的方法库。',
  },
];

function Page2Model() {
  return (
    <section className="v5d-page" id="v5d-sec-model">
      <PageHeader pageIndex={2} section="模型依据与报告说明" subtitle="了解本报告背后的模型来源、评估方式与使用建议" icon="file-text" />

      <div className="v5d-stack">
        {/* 1 · 报告依据 */}
        <div className="v5d-model-section">
          <h4><span className="v5d-section-num">1</span>报告依据</h4>
          <div className="v5d-model-grid-4">
            {MODEL_PILLARS.map((p) => (
              <div key={p.name} className="v5d-model-pillar">
                <h5>{p.name}</h5>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 2 · 模型结构 */}
        <div className="v5d-model-section">
          <h4><span className="v5d-section-num">2</span>模型结构</h4>
          <div className="v5d-model-grid-3">
            {MODEL_LAYERS.map((layer) => (
              <div key={layer.name} className="v5d-model-layer">
                <h5>{layer.name}</h5>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--v5-ink-2)', lineHeight: 1.65 }}>
                  {layer.question}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {layer.items.map((it) => (
                    <span
                      key={it}
                      style={{
                        background: 'var(--v5-accent-soft)',
                        color: 'var(--v5-accent-deep)',
                        fontSize: 11.5,
                        fontWeight: 600,
                        padding: '3px 9px',
                        borderRadius: 5,
                      }}
                    >
                      {it}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 3 · 结果立论说明 */}
        <div className="v5d-model-section">
          <h4><span className="v5d-section-num">3</span>结果立论说明</h4>
          <div className="v5d-grid-2">
            {MODEL_RESULT_NOTES.map((n) => (
              <div key={n.name} className="v5d-model-pillar">
                <h5>{n.name}</h5>
                <p>{n.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 4 · 使用提醒 */}
        <div className="v5d-model-section v5d-notice-card">
          <h4><span className="v5d-section-num">4</span>使用提醒</h4>
          <div className="v5d-notice-list">
            {MODEL_NOTICES.map((n) => (
              <div key={n.title} className="v5d-notice-item">
                <ReportIcon name="lightbulb" size={14} />
                <div>
                  <b>{n.title}：</b>
                  {renderInlineBold(n.body)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// 把文案里的 **xx** 渲染成 <b>
function renderInlineBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <b key={i}>{p.slice(2, -2)}</b>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// ===========================================================================
// Page 3 · 5 力深度诊断
// ===========================================================================
function Page3Abilities({ vm }) {
  // 详细报告里每个力的深度分析条目（adapter 输出在 vm.detailed.abilities，含
  // current_state_summary / impact_and_direction）；缺失时降级用 vm.abilities（基础字段）。
  const abilities = (vm.detailed && vm.detailed.abilities) || vm.abilities || [];
  return (
    <section className="v5d-page" id="v5d-sec-abilities">
      <PageHeader pageIndex={3} section="5 力深度诊断" subtitle="逐项分析你 5 项核心能力的当前得分、影响与提升方向" icon="bar-chart" />

      {abilities.map((a) => (
        <div key={a.code} className="v5d-deep-layer-block">
          <div key={a.code} className="v5d-deep-row">
            <div>
              <div className="v5d-deep-name">{a.name}</div>
              {/* 显示时期名（l_grade_label）而非旧 L 级编号 */}
              <span className={`v5d-l-chip ${(a.l_grade || 'L1').toLowerCase()}`}>
                {a.l_grade_label || a.l_grade || '—'}
              </span>
            </div>
            <div className="v5d-deep-score">
              <span className="v5d-pct">{a.percentage}</span>
              <span style={{ fontSize: 11, color: 'var(--v5-muted)', fontFamily: 'Consolas, monospace' }}>
                / 100
              </span>
            </div>
            <div className="v5d-deep-text">
              <p style={{ margin: '0 0 6px' }}><b>当前情况：</b>{a.current_state_summary || '—'}</p>
              <p style={{ margin: 0 }}><b>影响与提升方向：</b>{a.impact_and_direction || '—'}</p>
            </div>
          </div>
        </div>
      ))}

      <div className="v5d-deep-summary">
        <ReportIcon name="lightbulb" size={16} />
        <div>
          <b>重点关注建议：</b>从评分最低的 2 项能力入手补短板，配合优势能力做"以强带弱"的迁移，比平均用力更高效。
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// Page 4 · 管理场景应用建议（素质页永久移除后重新编号）
// ===========================================================================
function ScenarioTable({ variant, title, icon, reasonHead, usageHead, items }) {
  return (
    <div className={`v5d-scen-table-card ${variant}`}>
      <div className="v5d-card-title">
        <ReportIcon name={icon} size={16} /> {title}
      </div>
      <table className="v5d-scen-table">
        <thead>
          <tr>
            <th className="col-scen">场景</th>
            <th className="col-reason">{reasonHead}</th>
            <th className="col-usage">{usageHead}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s, i) => (
            <tr key={i}>
              <td className="v5d-scen-name">{s.name}</td>
              <td className="col-reason">{s.reason || '—'}</td>
              <td className="col-usage">{s.usage || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Page4Scenarios({ vm }) {
  return (
    <section className="v5d-page" id="v5d-sec-scenarios">
      <PageHeader pageIndex={4} section="管理场景应用建议" subtitle="基于你的优势和短板，给出管理场景上的 AI 使用边界与协作方式" icon="users" />

      <ScenarioTable
        variant="try-first"
        title="适合优先使用 AI 的管理场景"
        icon="thumbs-up"
        reasonHead="适合优先使用的原因"
        usageHead="推荐 AI 用法"
        items={vm.try_first_scenarios}
      />

      <ScenarioTable
        variant="cautious"
        title="需要人工深度介入的场景"
        icon="shield"
        reasonHead="需要人工深度介入的原因"
        usageHead="建议边界"
        items={vm.be_cautious_scenarios}
      />

      <div className="v5d-tip">
        <ReportIcon name="lightbulb" size={16} />
        <div>
          <b>温馨提示：</b>把"适合"表里的场景作为你的 AI 主战场；"需要人工深度介入"表里的场景，
          按"建议边界"保留人工把关 —— 把 AI 当辅助而不是终审。
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// Page 5 · 个性化提升路径（30 天计划）
// ===========================================================================
function Page5Growth({ vm }) {
  const g = vm.detailed.growth_path;
  return (
    <section className="v5d-page" id="v5d-sec-growth">
      <PageHeader pageIndex={5} section="个性化提升路径" subtitle="结合本次评估结果，为你量身定制的 30 天提升计划与后续建议" icon="award" />

      {g.overall_goal && (
        <div className="v5d-growth-goal" style={{ marginBottom: 14 }}>
          <ReportIcon name="award" size={18} />
          <div><b>30 天总目标：</b>{g.overall_goal}</div>
        </div>
      )}

      {/* 3 阶段 */}
      <div className="v5d-phase-row" style={{ marginBottom: 18 }}>
        {g.phases.map((p) => (
          <div key={p.phase_index} className="v5d-phase-card">
            <div className="v5d-phase-head">
              <span className="v5d-phase-index">阶段{['一', '二', '三'][p.phase_index - 1] || p.phase_index}</span>
              <span className="v5d-phase-label">{p.phase_label}</span>
            </div>
            <h4 className="v5d-phase-title">{p.phase_title}</h4>
            <div className="v5d-phase-goal">{p.phase_goal}</div>
            <ul className="v5d-phase-items">
              {p.learning_items.map((it, i) => <li key={i}>{it}</li>)}
            </ul>
            <div className="v5d-phase-focus">
              <span className="v5d-phase-focus-label">重点提升能力</span>
              {p.focus_abilities.map((a) => (
                <span key={a.code} className="v5d-phase-focus-chip">{a.name}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/*
        后续提升建议（vm.detailed.growth_path.follow_up_suggestions）暂时隐藏。
        预留位置给后期"我们的课程推荐"模块 —— 与外部课程系统对接后在此插入。
        view model 字段保留不删，方便后期切换。
      */}

      {/* 应用建议 */}
      {g.application_tips && (
        <div className="v5d-app-tips">
          <ReportIcon name="lightbulb" size={18} />
          <div><b>应用建议：</b>{g.application_tips}</div>
        </div>
      )}
    </section>
  );
}
